import axios from 'axios'
import { saveAs } from 'file-saver'
import AscentiaService from './ascentiaService'
import moment from 'moment'

const values = {
  maintenanceIssues: null,
  maintenanceIssuesByAirline: null,
  maintenanceIssuesStatuses: null,
  maintenanceIssue: null,
  maintenanceIssuePlots: null,
  maintenanceIssuesRelatedIssues: null,
  maintenanceIssuesRelatedAlerts: null,
  relatedAlertsAsUpdater: null,
  daysSinceSummary: null,
  componentSummary: null,
  statusSummary: null,
  maintenanceRecs: [],
  homePageMaintenanceIssues: null,
  mxRecAlertsExportGivenOptions: null,
}

let maintenanceIssuesByAirlineCall = null

const getters = {
  maintenanceIssues() {
    const url = `/MaintenanceIssue`
    return axios.get(url)
  },
  maintenanceGrid({ airlineIds, platforms }) {
    let url = `/MaintenanceIssue/grid?`
    if (airlineIds && airlineIds.length > 0) {
      for (const airline in airlineIds) {
        url += `&airlines=${airlineIds[airline]}`
      }
    }
    if (platforms && platforms.length > 0) {
      for (const platform in platforms) {
        url += `&masterModels=${platforms[platform]}`
      }
    }
    return axios.get(url)
  },
  maintenanceIssueById({ issueId }) {
    const url = `/MaintenanceIssue/${issueId}`
    return axios.get(url)
  },
  analyticFeatures({ alertTypes }) {
    const list = alertTypes.map(alertType => {
      return axios.get(
        `/Feature?filterBy=alerttype&filterById=${alertType.alertTypeId}`,
      )
    })
    return axios.all(list)
  },
  analyticRiskScores({ featureIds }) {
    const list = featureIds.map(id => {
      return axios.get(`/Feature?filterBy=feature&filterById=${id}`)
    })
    return axios.all(list)
  },
  maintenanceIssuesByAirline(selectedAirlines) {
    if (maintenanceIssuesByAirlineCall) {
      maintenanceIssuesByAirlineCall.cancel(
        'Only one group request allowed at a time.',
      )
    }
    maintenanceIssuesByAirlineCall = axios.CancelToken.source()
    const list = selectedAirlines.map(airline => {
      return axios.get(`/MaintenanceIssue?airline=${airline}`, {
        cancelToken: maintenanceIssuesByAirlineCall.token,
      })
    })
    return axios.all(list)
  },
  maintenanceIssuesStatuses() {
    return axios.get(`/MaintenanceIssue/Statuses`)
  },
  maintenanceIssuesRelatedIssues({ ataChapter, tail, isIssueUpdater }) {
    // TODO: include issue sources here for chronic repeaters
    const url = `/MaintenanceIssue?aircraftId=${tail}&ataChapter=${ataChapter}&isIssueUpdater=${isIssueUpdater}`
    return axios.get(url)
  },
  daysSinceSummary({ airlines, platforms }) {
    let url = `/MaintenanceIssue?`
    url += `page=0&page_size=10&orderBy=datesubmitted&sortOrder=ascending`
    if (airlines && airlines.length > 0) {
      for (const airline in airlines) {
        url += `&airlines=${airlines[airline]}`
      }
    }
    if (platforms && platforms.length > 0) {
      for (const platform in platforms) {
        url += `&masterModels=${platforms[platform]}`
      }
    }
    return axios.get(url)
  },
  homePageMaintenanceIssues({ airlineIds, platforms }) {
    let url = `/MaintenanceIssue/grid?`
    url += `page=0&page_size=10&orderBy=lastalertdate&sortOrder=descending`
    if (airlineIds && airlineIds.length > 0) {
      for (const airline in airlineIds) {
        url += `&airlines=${airlineIds[airline]}`
      }
    }
    if (platforms && platforms.length > 0) {
      for (const platform in platforms) {
        url += `&masterModels=${platforms[platform]}`
      }
    }
    return axios.get(url)
  },
  componentSummary({ airlines, platforms }) {
    let url = `/MaintenanceIssue/summary?groupby=component`
    if (airlines && airlines.length > 0) {
      for (const airline in airlines) {
        url += `&airlineId=${airlines[airline]}`
      }
    }
    url += `&dateStart=2000-01-01`
    if (platforms && platforms.length > 0) {
      for (const platform in platforms) {
        url += `&masterModels=${platforms[platform]}`
      }
    }
    return axios.get(url)
  },
  statusSummary({ airlines, platforms }) {
    let url = `/MaintenanceIssue/summary?groupBy=status`
    if (airlines && airlines.length > 0) {
      for (const airline in airlines) {
        url += `&airlineId=${airlines[airline]}`
      }
    }
    url += `&dateStart=2000-01-01`
    if (platforms && platforms.length > 0) {
      for (const platform in platforms) {
        url += `&masterModels=${platforms[platform]}`
      }
    }
    return axios.get(url)
  },
  maintenanceIssueLockStatus({ issueId }) {
    const url = `/MaintenanceIssue/lockstatus?maintenanceIssueId=${issueId}`
    return axios.get(url)
  },

  customAlertMaintenanceIssueVersion({ issueId }) {
    const url = `/MaintenanceIssue/relatedVersion/${issueId}`
    return axios.get(url)
  },
  airlineResponseOptions() {
    const url = `/MaintenanceIssue/airlineresponsetypes`
    return axios.get(url)
  },
  reasonToCloseOptions() {
    const url = `/MaintenanceIssue/closingreasontypes`
    return axios.get(url)
  },
  fixEffectivenessOptions() {
    const url = `/MaintenanceIssue/fixeffectivenesstypes`
    return axios.get(url)
  },
  analyticDetectionOptions() {
    const url = `/MaintenanceIssue/analyticdetectionoutcometypes`
    return axios.get(url)
  },
  serviceDeliveryOutcomeOptions() {
    const url = `/MaintenanceIssue/servicedeliveryoutcometypes`
    return axios.get(url)
  },
  severityOptions() {
    const url = `/MaintenanceIssue/severitytypes`
    return axios.get(url)
  },
  mxRecAlertsExportGivenOptions({
    startDate,
    endDate,
    airlines,
    masterModels,
    dateFilterOn,
  }) {
    let queryString = `startDate=${startDate}&endDate=${endDate}&dateFilterOn=${dateFilterOn}`

    if (airlines.length > 0) {
      airlines.forEach(item => {
        queryString += `&airlines=${item}`
      })
    }

    if (masterModels.length > 0) {
      masterModels.forEach(item => {
        queryString += `&masterModels=${item}`
      })
    }

    const url = `/MaintenanceIssue/ExportMaintenanceIssues?${queryString}`

    return axios
      .get(url, {
        headers: { Accept: 'application/vnd.ms-excel' },
        responseType: 'blob',
      })
      .then(response => {
        const formattedDate = moment().format('YYYY-MM-DD_HH-mm-ss')
        const fileName = `Export_MxRecAlerts_${formattedDate}.xlsx`
        saveAs(response.data, fileName)
      })
  },
  issueSourceOptions() {
    const url = `/MaintenanceIssue/sources`
    return axios.get(url)
  },
}

const saves = {
  maintenanceIssue(issue) {
    if (issue.id) {
      return axios.put(`/MaintenanceIssue/${issue.id}`, issue)
    } else {
      return axios.post(`/MaintenanceIssue`, issue)
    }
  },

  maintenanceIssuePlots({ issueId, plotId }) {
    if (issueId && plotId) {
      // Remove return and uncomment this line when available
      return axios.put(`/MaintenanceIssue/${issueId}/Plot/${plotId}`)
    }
  },

  maintenanceIssueLockStatus({ issueId, setLock }) {
    if (issueId) {
      return axios.put(
        `/MaintenanceIssue/lockstatus?maintenanceIssueId=${issueId}&setLock=${setLock}`,
      )
    }
  },

  repeaterIssue(issue) {
    return axios.post('/MaintenanceIssue/newRepeaterIssue', issue)
  },
}

const deletes = {
  maintenanceIssuePlot({ issueId, plotId }) {
    if (issueId && plotId) {
      return axios.delete(`/MaintenanceIssue/${issueId}/Plot/${plotId}`)
    }
  },
}

export default class MaintenanceIssueService extends AscentiaService {
  get values() {
    return values
  }

  get getters() {
    return getters
  }

  get saves() {
    return saves
  }

  get deletes() {
    return deletes
  }
}
